import { List, ListItem, Popover } from '@mui/material'
import { FC } from 'react'
import { useNavigate, useLocation } from 'react-router'

import { CardActionButton } from '../../../components'
import { useAppDispatch } from '../../../hooks'
import {
  getBillTillTodayPDFAction,
} from '../../../store/items'
import {
  Bill,
  BillPDFRequest,
  reapplyHsnSacCode,
  reapplyItemArea,
  resetWeightValue,
} from '../../../services'
import { CurrentAccountMoreActionsEnum } from '../../../services/types'
import { refreshFetchSlipsAction } from '../../../store/slips'
import { showAlertAction } from '../../../store/alerts'

export interface MoreActionsProps {
  onClose?: () => void
  onChangeTab: (tab) => void
  bill: Bill
  myref: any
  showMore: boolean
  setShowMoreAction?: (string) => void
  currentTab?: string
}

export const MoreActions: FC<MoreActionsProps> = ({
  onClose,
  bill,
  myref,
  onChangeTab,
  showMore,
  setShowMoreAction,
  currentTab,
}) => {
  const dispatchAction = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const handleViewPDF = (request: BillPDFRequest) => {
    dispatchAction(getBillTillTodayPDFAction(request))
  }

  const handleReapplyItemArea = async () => {
    try {
      setShowMore(false)
      const response = await reapplyItemArea(bill?.id)
      
      if (response?.data?.success) {
        // Check if on ViewSlips page
        if (location.pathname.includes('/slips/view-slips')) {
          // Refresh slipdata without flickering
          dispatchAction(refreshFetchSlipsAction())
        }
        dispatchAction(
          showAlertAction({
            message: response.data.message || 'Item area reapplied successfully',
            severity: 'success',
          })
        )
      } else {
        dispatchAction(
          showAlertAction({
            message: response?.data?.message || 'Failed to reapply item area',
            severity: 'error',
          })
        )
      }
    } catch (error: any) {
      dispatchAction(
        showAlertAction({
          message: error?.response?.data?.message || 'An error occurred while reapplying item area',
          severity: 'error',
        })
      )
    }
  }

  const handleReapplyWeightValueOnEntries = async () => {
    try {
      setShowMore(false)
      const response = await resetWeightValue(bill?.id)

      if (response?.data?.success) {
        if (location.pathname.includes('/slips/view-slips')) {
          dispatchAction(refreshFetchSlipsAction())
        }
        dispatchAction(
          showAlertAction({
            message:
              response.data.message ||
              'Weight and value reapplied on entries successfully',
            severity: 'success',
          })
        )
      } else {
        dispatchAction(
          showAlertAction({
            message:
              response?.data?.message || 'Failed to reapply weight and value',
            severity: 'error',
          })
        )
      }
    } catch (error: any) {
      dispatchAction(
        showAlertAction({
          message:
            error?.response?.data?.message ||
            'An error occurred while reapplying weight and value',
          severity: 'error',
        })
      )
    }
  }

  const handleReapplyHsnSacCode = async () => {
    try {
      setShowMore(false)
      const response = await reapplyHsnSacCode(bill?.id)

      if (response?.data?.success) {
        // Check if on ViewSlips page
        if (location.pathname.includes('/slips/view-slips')) {
          // Refresh slipdata without flickering
          dispatchAction(refreshFetchSlipsAction())
        }
        dispatchAction(
          showAlertAction({
            message: response.data.message || 'SAC code reapplied successfully',
            severity: 'success',
          })
        )
      } else {
        dispatchAction(
          showAlertAction({
            message:
              response?.data?.message || 'Failed to reapply SAC code',
            severity: 'error',
          })
        )
      }
    } catch (error: any) {
      dispatchAction(
        showAlertAction({
          message:
            error?.response?.data?.message ||
            'An error occurred while reapplying SAC code',
          severity: 'error',
        })
      )
    }
  }

  const setShowMore = (value) => {
    onClose()
  }
  return (
    <>
      <Popover
        open={showMore}
        anchorEl={myref?.current}
        onClose={() => {
          onClose()
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List sx={{ p: 0 }} component="nav">
          <ListItem
            id={`view-${
              bill?.status === 'OPEN'
                ? 'view_bill_till_today_list_item'
                : 'view_bill_list_item'
            }`}
          >
            <CardActionButton
              buttonText={
                bill?.status === 'OPEN' ? 'Proforma - Bill Till Today' : 'View Bill'
              }
              data={true}
              icon={'receipt'}
              onClickHandler={() => {
                if (bill?.totalCeilingArea > 0) {
                  setShowMoreAction(
                    CurrentAccountMoreActionsEnum.SHOW_PLOT_AREA_DATE_PICKER
                  )
                } else {
                  handleViewPDF({ billId: bill?.id })
                }
                setShowMore(false)
              }}
            />
          </ListItem>
          <ListItem id="view_bill_between_dates_list_item">
            <CardActionButton
              buttonText="Proforma - Bill by Date Range"
              data={true}
              icon={'dateRange'}
              onClickHandler={() => {
                setShowMore(false)
                setShowMoreAction(
                  CurrentAccountMoreActionsEnum.BILL_BETWEEN_TWO_DATES
                )
              }}
            />
          </ListItem>
          {bill?.status === 'OPEN' && (
            <>
              <ListItem id="create_bill_list_item">
                <CardActionButton
                  buttonText="Bills"
                  data={true}
                  icon={'saveAs'}
                  onClickHandler={() => {
                    setShowMore(false)
                    navigate({
                      pathname: `/slips/view-slips/${bill?.id}/${bill?.customer.id}`,
                      search: '?tab_index=6',
                    })
                    onChangeTab(6)
                  }}
                />
              </ListItem>
              <ListItem id="view_or_add_payment_list_item">
                <CardActionButton
                  buttonText="View or Receive Payment"
                  data={true}
                  icon={'quote'}
                  onClickHandler={() => {
                    setShowMore(false)
                    navigate({
                      pathname: `/slips/view-slips/${bill?.id}/${bill?.customer.id}`,
                      search: '?tab_index=4',
                    })
                    onChangeTab(4)
                  }}
                />
              </ListItem>
              <ListItem id="add_other_charges_list_item">
                <CardActionButton
                  buttonText="Add Other Charges"
                  data={true}
                  icon={'other_charge'}
                  onClickHandler={() => {
                    setShowMoreAction(
                      CurrentAccountMoreActionsEnum.ADD_OTHER_CHARGES
                    )
                    setShowMore(false)
                  }}
                  disabled={false}
                />
              </ListItem>

              <ListItem
                sx={{ borderBottom: 1, borderColor: 'lightgray' }}
                id="lost_or_damaged_charged_list_item"
              >
                <CardActionButton
                  buttonText="Lost or Damaged Charges"
                  data={true}
                  icon={'lost'}
                  onClickHandler={() => {
                    // Track lost items clicked - we'll need to pass tracker or track in parent
                    setShowMoreAction(
                      CurrentAccountMoreActionsEnum.LOST_OR_DAMAGE_CHARGES
                    )
                    setShowMore(false)
                  }}
                  disabled={false}
                />
              </ListItem>
            </>
          )}
          <ListItem id="create_new_task">
            <CardActionButton
              buttonText="Create New Task"
              data={true}
              icon={'create_new_task'}
              onClickHandler={() => {
                setShowMoreAction(CurrentAccountMoreActionsEnum.CREATE_NEW_TASK)
                setShowMore(false)
              }}
              disabled={false}
            />
          </ListItem>
          {bill?.status != 'CLOSED' && (
            <ListItem id="edit_account_list_item">
              <CardActionButton
                buttonText="Edit Site Details"
                data={true}
                icon={'edit'}
                onClickHandler={() => {
                  setShowMoreAction(CurrentAccountMoreActionsEnum.EDIT_ACCOUNT)
                  setShowMore(false)
                }}
              />
            </ListItem>
          )}
          {bill?.status != 'CLOSED' && (
            <ListItem id="edit_party_details_list_item">
              <CardActionButton
                buttonText="Edit Party Details"
                data={true}
                icon={'edit'}
                onClickHandler={() => {
                  setShowMoreAction(
                    CurrentAccountMoreActionsEnum.EDIT_PARTY_DETAILS
                  )
                  setShowMore(false)
                }}
              />
            </ListItem>
          )}
          {bill?.status != 'OPEN' && (
            <ListItem id="reopen_account_list_item">
              <CardActionButton
                buttonText={currentTab === 'DEFAULTER' ? 'Reopen Site' : 'Reopen Account'}
                data={true}
                icon={'file_open'}
                onClickHandler={() => {
                  setShowMoreAction(
                    CurrentAccountMoreActionsEnum.REOPEN_ACCOUNT
                  )
                  setShowMore(false)
                }}
              />
            </ListItem>
          )}
          {currentTab !== 'DEFAULTER' && bill?.status !== 'DEFAULTER' && (
            <ListItem id="mark_defaulter_list_item">
              <CardActionButton
                buttonText="Mark Defaulter"
                data={true}
                icon={'file_open'}
                onClickHandler={() => {
                  setShowMoreAction(
                    CurrentAccountMoreActionsEnum.MARK_DEFAULTER
                  )
                  setShowMore(false)
                }}
              />
            </ListItem>
          )}

          {bill?.status != 'CLOSED' && bill?.remainingPieces > 0 && (
            <ListItem id="move_balance_item_list_item">
              <CardActionButton
                buttonText="Move balance items to new account"
                data={true}
                icon={'file_open'}
                onClickHandler={() => {
                  setShowMoreAction(
                    CurrentAccountMoreActionsEnum.MOVE_BALANCE_TO_NEW_ACCOUNT
                  )
                  setShowMore(false)
                }}
              />
            </ListItem>
          )}

          {bill?.status != 'CLOSED' && (
            <ListItem id="close_account_list_item">
              <CardActionButton
                buttonText="Close Site"
                data={true}
                icon={'dnd'}
                onClickHandler={() => {
                  setShowMoreAction(CurrentAccountMoreActionsEnum.CLOSE_ACCOUNT)
                  setShowMore(false)
                }}
              />
            </ListItem>
          )}

          {bill?.status === 'OPEN' && (
            <ListItem id="bill_settings_list_item">
              <CardActionButton
                buttonText="One Day Discount Settings"
                data={true}
                icon={'settings'}
                onClickHandler={() => {
                  setShowMoreAction(
                    CurrentAccountMoreActionsEnum.ONE_DAY_DISCOUNT_SETTING
                  )
                  setShowMore(false)
                }}
              />
            </ListItem>
          )}

          {bill?.status === 'OPEN' && (
            <ListItem id="reapply_item_area_list_item">
              <CardActionButton
                buttonText="Update Item Area from Godown Stock"
                data={true}
                icon={'refresh'}
                onClickHandler={handleReapplyItemArea}
                disabled={false}
              />
            </ListItem>
          )}
          {bill?.status === 'OPEN' && (
            <ListItem id="reset_weight_value_list_item">
              <CardActionButton
                buttonText="Update Weight & Cost from Godown Stock"
                data={true}
                icon={'refresh'}
                onClickHandler={handleReapplyWeightValueOnEntries}
                disabled={false}
              />
            </ListItem>
          )}
          {bill?.status === 'OPEN' && (
            <ListItem id="reapply_hsn_sac_code_list_item">
              <CardActionButton
                buttonText="Update SAC Code from Godown Stock"
                data={true}
                icon={'refresh'}
                onClickHandler={handleReapplyHsnSacCode}
                disabled={false}
              />
            </ListItem>
          )}

          <ListItem id="delete_account_list_item">
            <CardActionButton
              id="id_more_option_delete_account"
              buttonText="Delete"
              data={true}
              icon={'delete'}
              onClickHandler={() => {
                setShowMoreAction(CurrentAccountMoreActionsEnum.DELETE)
                setShowMore(false)
              }}
            />
          </ListItem>
        </List>
      </Popover>
    </>
  )
}
